import { Checkbox, InputLabel, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useContext, useMemo } from 'react';
import WarningPopup from '../../../Popups/WarningPopup';
import { useTripRateContext } from '../../TripRateContext';
import { SessionContext } from '../../../Contexts/SessionContext';
import { CounterBadge } from '../../../Utilities/CounterBadge';

const roundToTwoDecimalPlaces = (value: number) => Math.round(value * 100) / 100;
const roundDownToTwoDecimalPlaces = (value: number) => Math.floor(value * 100) / 100;
const roundUpToTwoDecimalPlaces = (value: number) => Math.ceil(value * 100) / 100;


export default function PercentDwellingsPrivatelyOwnedRange() {
    const { surveys } = useTripRateContext();
    const { sessionState, setSessionField } = useContext(SessionContext);

    // Calculate min/max for percentage of privately owned dwellings
    const minMaxParam = useMemo(() => {
        if (!Array.isArray(surveys) || surveys.length === 0) return null;

        const validValues = surveys
            .map(s => s.privatePercentage)
            .filter(v => typeof v === 'number' && !isNaN(v)) as number[];

        if (validValues.length === 0) return null;

        const min = roundDownToTwoDecimalPlaces(Math.min(...validValues));
        const max = roundUpToTwoDecimalPlaces(Math.max(...validValues));
        const avg = validValues.reduce((a, b) => a + b, 0) / validValues.length;

        setSessionField('primaryFilters.minPercentDwellings', min);
        setSessionField('primaryFilters.maxPercentDwellings', max);

        return { min, max, avg };
    }, [surveys]);

    const percentDwellingsChecked =
        sessionState?.primaryFilters?.percentDwellingsChecked ?? true;

    const min = sessionState?.primaryFilters?.minPercentDwellings ?? '';
    const max = sessionState?.primaryFilters?.maxPercentDwellings ?? '';

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSessionField('primaryFilters.percentDwellingsChecked', e.target.checked);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const parsed = value === '' ? '' : roundToTwoDecimalPlaces(Number(value));

        if (name === 'minPercentDwellingsPrivate') {
            setSessionField('primaryFilters.minPercentDwellings', parsed);
        } else if (name === 'maxPercentDwellingsPrivate') {
            setSessionField('primaryFilters.maxPercentDwellings', parsed);
        }
    };

    return (
        <Grid container component={Paper} size={12} alignItems="center" sx={{ position: 'relative' }}>
            <CounterBadge value={'7'} />

            <Grid size={12}>
                <Typography textAlign='center'>Percentage of Dwellings Privately Owned<WarningPopup
                    title="Warning on Private Ownership Percentage"
                    content="By default, all surveys are included. Unticking this box will limit results to sites where the percentage of privately owned dwellings is known and within the specified range."
                    type="warning"
                /></Typography>

            </Grid>

            <Grid size={6}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <InputLabel id="percentDwellingsPrivateLabel">
                        Include All Surveys
                    </InputLabel>
                    <Checkbox
                        name="percentDwellingsChecked"
                        checked={percentDwellingsChecked}
                        onChange={handleCheckboxChange}
                    />
                </Stack>
            </Grid>

            <Grid container size={12}>
                <Grid size={4}>
                    <Stack spacing={3}>
                        <Typography variant="body1">
                            Minimum: {minMaxParam?.min ?? '�'}%
                        </Typography>
                        <Typography variant="body1">
                            Maximum: {minMaxParam?.max ?? '�'}%
                        </Typography>
                    </Stack>
                </Grid>

                {!percentDwellingsChecked && (
                    <Grid size={8}>
                        <Stack spacing={1}>
                            <Grid size={12}>
                                <TextField
                                    name="minPercentDwellingsPrivate"
                                    value={min}
                                    onChange={handleInputChange}
                                    label="Minimum (%)"
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    name="maxPercentDwellingsPrivate"
                                    value={max}
                                    onChange={handleInputChange}
                                    label="Maximum (%)"
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                />
                            </Grid>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}
